import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import HelpPane from 'components/Common/HelpPane'
import Modal from 'LuxKit/Modal/Modal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'

const Divider = styled.hr`
  grid-column: span 2;
  width: 100%;
  height: 1px;
  margin: 0;
  background-color: ${props => props.theme.palette.neutral.default.six};
  border: none;
`

const PolicyGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-row-gap: ${rem(16)};
  grid-column-gap: ${rem(24)};
  margin-top: ${rem(40)};
`

interface Props {
  cancellationInfo?: App.ExperienceItemCancellationInfo
  date?: string
  onSupportEmailClick?: () => void
  onSupportNumberClick?: () => void
}

function ExperienceCancellationPolicyModal(props: Props) {
  const { cancellationInfo, onSupportEmailClick, onSupportNumberClick } = props

  const policies = useMemo(() => {
    const policyMap = (cancellationInfo?.refundPolicies ?? []).reduce<Record<string, Array<App.ExperienceItemRefundPolicy>>>((acc, policy) => {
      const key = policy.groupId ?? 1
      if (!acc[key]) acc[key] = []
      acc[key].push(policy)
      return acc
    }, {})

    return Object.entries(policyMap)
  }, [cancellationInfo?.refundPolicies])

  return (<Modal title="Cancellation policy">
    {cancellationInfo && (
      <>
        <BodyText className="full-width" variant="large">
          {cancellationInfo.text}
        </BodyText>
        {policies.length > 0 &&
          <PolicyGrid>
            {policies.map(([_, policies], index) => {
              const refundValue = Math.min(...policies.map(policy => policy.value))
              return (
                <React.Fragment key={index}>
                  <VerticalSpacer gap={8}>
                    {policies.map(policy => (
                      <BodyText variant="medium" weight="bold" key={policy.id}>
                        {policy.periodLabel}
                      </BodyText>
                    ))}
                  </VerticalSpacer>
                  <Group direction="vertical" verticalAlign="center">
                    {refundValue > 0 && <BodyText variant="medium">
                      {refundValue}% refund
                    </BodyText>}
                    {refundValue === 0 && <BodyText variant="medium">
                      Non-refundable
                    </BodyText>}
                  </Group>
                  <Divider />
                </React.Fragment>
              )
            })}
            <React.Fragment key={policies.length}>
              <BodyText variant="medium" weight="bold">
                Outside of other cancellation policies
              </BodyText>
              <Group direction="vertical" verticalAlign="center">
                <BodyText variant="medium">
                  Non-refundable
                </BodyText>
              </Group>
            </React.Fragment>
          </PolicyGrid>}
      </>
    )}
    <HelpPane onSupportEmailClick={onSupportEmailClick} onSupportNumberClick={onSupportNumberClick} />
  </Modal>)
}

export default ExperienceCancellationPolicyModal
